import { getProductPage } from "../../../config/productContent.js";
import { productDetailRoute } from "../../../config/productDetails.js";
import { publicAsset } from "../../../config/paths.js";
import { buildAnimatedHeading } from "../../HeroTitleAnimation.js";
import { buildSectionPillRow } from "../../components/SectionPill.js";

function chunkPairs(cards = []) {
  const pairs = [];
  for (let i = 0; i < cards.length; i += 2) {
    pairs.push(cards.slice(i, i + 2));
  }
  return pairs.length ? pairs : [[]];
}

function renderCard(card, index) {
  const lean = index % 2 === 0 ? "left" : "right";
  const imageSrc = card.image ? publicAsset(card.image) : "";
  const href = card.slug ? productDetailRoute(card.slug) : "#";

  return `
    <a
      class="product-showcase__card product-showcase__card--lean-${lean}"
      style="--card-i: ${index}"
      href="${href}"
      aria-label="View details: ${card.title}"
    >
      <div class="product-showcase__card-visual">
        <div class="product-showcase__card-orbit">
          <div class="product-showcase__card-frame">
            ${
              imageSrc
                ? `<img class="product-showcase__card-img" src="${imageSrc}" alt="" loading="lazy" decoding="async" />`
                : `<div class="product-showcase__card-img product-showcase__card-img--placeholder" role="presentation"></div>`
            }
          </div>
        </div>
      </div>
      <div class="product-showcase__card-body">
        <span class="product-showcase__card-category">${card.category}</span>
        <h3 class="product-showcase__card-title">${card.title}</h3>
        <p class="product-showcase__card-excerpt">${card.excerpt}</p>
        <span class="product-showcase__card-more">View details <span aria-hidden="true">→</span></span>
      </div>
    </a>
  `;
}

function renderSummary(summary) {
  const paragraphs = Array.isArray(summary) ? summary : [summary];
  return paragraphs
    .filter(Boolean)
    .map((text) => `<p class="product-showcase__service-summary">${text}</p>`)
    .join("");
}

function renderLane(service, index) {
  const pairs = chunkPairs(service.cards);
  const side = index % 2 === 0 ? "left" : "right";
  const number = String(index + 1).padStart(2, "0");

  return `
    <div
      class="product-showcase__lane product-showcase__lane--${side}"
      id="${service.id}"
      data-lane
      data-lane-index="${index}"
      style="--lane-steps: ${pairs.length}"
    >
      <div class="product-showcase__lane-pin">
        <div class="product-showcase__sticky">
          <div class="product-showcase__viewport">
            <aside class="product-showcase__service-panel">
              <span class="product-showcase__service-index">${number}</span>
              <h3 class="product-showcase__service-title">${service.label}</h3>
              ${renderSummary(service.summary)}
              <a class="btn btn--ghost product-showcase__cta" href="${service.href}">
                Explore ${service.label}
              </a>
            </aside>

            <div class="product-showcase__stage">
              <div class="product-showcase__stage-head">
                <span class="product-showcase__eyebrow">${service.label}</span>
                <span class="product-showcase__step" data-lane-step>1 / ${pairs.length}</span>
              </div>
              <div class="product-showcase__pair" data-lane-pair>
                ${pairs[0].map(renderCard).join("")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

export function createProductShowcaseSection() {
  const { showcase } = getProductPage();
  const services = showcase.services || [];

  const section = document.createElement("section");
  section.id = "showcase";
  section.className = "section product-showcase";

  section.innerHTML = `
    <div class="product-showcase__intro-shell">
      <div class="product-showcase__intro-wrap">
        <div class="product-showcase__intro reveal">
          ${buildSectionPillRow(showcase.badge)}
          <h2>${buildAnimatedHeading(showcase.titleSegments)}</h2>
          <p>${showcase.subtitle}</p>
        </div>
      </div>
    </div>
    <div class="product-showcase__lanes">
      ${services.map((service, index) => renderLane(service, index)).join("")}
    </div>
  `;

  bindLanes(section, services);
  return section;
}

function bindLanes(section, services) {
  const lanes = [...section.querySelectorAll("[data-lane]")];
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const compactQuery = window.matchMedia("(max-width: 899px)");
  let ticking = false;
  const EXIT_MS = 280;
  const ENTER_MS = 480;

  const laneState = lanes.map((lane, index) => {
    const pairs = chunkPairs(services[index]?.cards || []);
    return {
      lane,
      pairs,
      pairEl: lane.querySelector("[data-lane-pair]"),
      stepEl: lane.querySelector("[data-lane-step]"),
      stageEl: lane.querySelector(".product-showcase__stage"),
      activePair: -1,
      animating: false,
      queued: null,
    };
  });

  const renderPairHtml = (pair) => pair.map(renderCard).join("");
  const allCardsOf = (state) => state.pairs.flat();

  const renderCompact = (state) => {
    if (!state.pairEl) return;
    const cards = allCardsOf(state);
    state.activePair = -1;
    state.animating = false;
    state.queued = null;
    state.pairEl.innerHTML = cards.map(renderCard).join("");
    state.pairEl.dataset.ready = "1";
    state.pairEl.dataset.mode = "compact";
    state.pairEl.classList.remove("is-exiting", "is-entering");
    if (state.stepEl) state.stepEl.textContent = String(cards.length);
  };

  const enterPair = (state, pairIndex) => {
    if (!state.pairEl) return;
    state.activePair = pairIndex;
    if (state.stepEl) {
      state.stepEl.textContent = `${pairIndex + 1} / ${state.pairs.length}`;
    }

    state.pairEl.innerHTML = renderPairHtml(state.pairs[pairIndex]);
    state.pairEl.dataset.ready = "1";
    state.pairEl.classList.remove("is-exiting", "is-entering");
    void state.pairEl.offsetWidth;
    state.pairEl.classList.add("is-entering");
    state.stageEl?.classList.add("is-flash");

    window.setTimeout(() => {
      state.pairEl?.classList.remove("is-entering");
      state.stageEl?.classList.remove("is-flash");
      state.animating = false;
      if (state.queued !== null && state.queued !== state.activePair) {
        const next = state.queued;
        state.queued = null;
        applyPair(state, next, { animate: true });
      }
    }, ENTER_MS);
  };

  const applyPair = (state, pairIndex, { animate = true } = {}) => {
    if (compactQuery.matches) {
      renderCompact(state);
      return;
    }

    const next = Math.max(0, Math.min(state.pairs.length - 1, pairIndex));
    if (!state.pairEl) return;
    state.pairEl.dataset.mode = "pairs";

    if (next === state.activePair && state.pairEl.dataset.ready === "1") {
      if (state.stepEl) state.stepEl.textContent = `${next + 1} / ${state.pairs.length}`;
      return;
    }

    if (state.animating) {
      state.queued = next;
      return;
    }

    if (!animate || reduced || state.pairEl.dataset.ready !== "1") {
      state.activePair = next;
      if (state.stepEl) state.stepEl.textContent = `${next + 1} / ${state.pairs.length}`;
      state.pairEl.innerHTML = renderPairHtml(state.pairs[next]);
      state.pairEl.dataset.ready = "1";
      state.pairEl.classList.remove("is-exiting", "is-entering");
      return;
    }

    state.animating = true;
    state.queued = null;
    state.pairEl.classList.remove("is-entering");
    state.pairEl.classList.add("is-exiting");
    state.stageEl?.classList.add("is-draining");

    window.setTimeout(() => {
      state.stageEl?.classList.remove("is-draining");
      enterPair(state, next);
    }, EXIT_MS);
  };

  const syncLane = (state) => {
    if (compactQuery.matches) {
      if (state.pairEl?.dataset.mode !== "compact") renderCompact(state);
      return;
    }

    if (state.pairEl?.dataset.mode === "compact") {
      state.pairEl.dataset.mode = "pairs";
      state.activePair = -1;
      applyPair(state, 0, { animate: false });
    }

    const pin = state.lane.querySelector(".product-showcase__lane-pin");
    if (!pin) return;

    const rect = pin.getBoundingClientRect();
    const inView =
      rect.bottom > -window.innerHeight * 0.25 &&
      rect.top < window.innerHeight * 1.25;
    if (!inView) return;

    const navBar = document.querySelector(".nav__bar");
    const stickTop = navBar ? navBar.offsetHeight : 72;
    const stickyHeight = Math.max(1, window.innerHeight - stickTop);
    const totalScroll = pin.offsetHeight - stickyHeight;
    if (totalScroll <= 0) {
      applyPair(state, 0, { animate: false });
      return;
    }

    const progress = Math.min(1, Math.max(0, (stickTop - rect.top) / totalScroll));
    const index = Math.min(
      state.pairs.length - 1,
      Math.floor(progress * state.pairs.length + 1e-6)
    );
    applyPair(state, index);
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      laneState.forEach(syncLane);
      ticking = false;
    });
  };

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll);
  compactQuery.addEventListener("change", onScroll);
  laneState.forEach((state) => {
    if (compactQuery.matches) renderCompact(state);
    else applyPair(state, 0, { animate: false });
  });
  onScroll();
}
